import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { CalendarX2, Check } from "lucide-react";
import { fullDate } from "@/components/Booking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CancelPage({ token }) {
  const [state, setState] = useState("loading"); // loading | found | canceling | canceled | error
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/bookings/lookup`, { params: { token } })
      .then((res) => {
        setBooking(res.data.booking);
        setState("found");
      })
      .catch(() => setState("error"));
  }, [token]);

  const cancel = async () => {
    setState("canceling");
    try {
      await axios.post(`${API}/bookings/cancel`, { token });
      setState("canceled");
    } catch {
      setState("error");
    }
  };

  return (
    <section className="px-6 py-16 text-center" data-testid="cancel-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {state === "loading" && (
          <p className="text-sm text-stone-500" data-testid="cancel-loading">
            Carregando agendamento...
          </p>
        )}

        {state === "error" && (
          <div data-testid="cancel-error">
            <h2 className="font-display text-2xl text-amber-100">
              Agendamento não encontrado
            </h2>
            <p className="mt-3 text-sm text-stone-400">
              Este agendamento já foi cancelado ou o link é inválido.
            </p>
            <a href="/" className="gold-btn mt-8 w-full" data-testid="cancel-home-link">
              Voltar ao início
            </a>
          </div>
        )}

        {(state === "found" || state === "canceling") && booking && (
          <div data-testid="cancel-details">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-2 border-amber-400/60">
              <CalendarX2 size={34} className="text-amber-400" />
            </div>
            <h2 className="mt-6 font-display text-3xl text-amber-100">
              Cancelar <span className="gold-text italic">agendamento?</span>
            </h2>
            <p className="mt-3 text-sm text-stone-400">
              O horário ficará livre para outro cliente na hora.
            </p>

            <dl className="mt-8 divide-y divide-amber-500/10 rounded-2xl border border-amber-500/20 bg-white/[0.02] text-left">
              {[
                ["Data", fullDate(booking.date)],
                ["Horário", booking.time],
                ["Serviço", booking.service],
                ["Cliente", booking.name],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-4 px-5 py-4">
                  <dt className="text-xs text-stone-500">{label}</dt>
                  <dd className="text-right text-sm font-medium text-amber-100">{value}</dd>
                </div>
              ))}
            </dl>

            <button
              type="button"
              onClick={cancel}
              disabled={state === "canceling"}
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-2xl border border-red-500/40 bg-red-500/10 px-6 py-4 font-display text-lg font-bold text-red-300 transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-red-400/70 hover:bg-red-500/20 disabled:pointer-events-none disabled:opacity-40"
              data-testid="cancel-confirm-button"
            >
              {state === "canceling" ? "Cancelando..." : "Sim, cancelar agendamento"}
            </button>
            <a
              href="/"
              className="mt-4 inline-block text-xs uppercase tracking-[0.2em] text-stone-500 transition-colors duration-300 hover:text-amber-300"
              data-testid="cancel-keep-link"
            >
              Manter meu horário
            </a>
          </div>
        )}

        {state === "canceled" && (
          <div data-testid="cancel-success">
            <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border-2 border-amber-400/80 shadow-[0_0_40px_rgba(212,175,55,0.2)]">
              <Check size={44} strokeWidth={2} className="text-amber-400" />
            </div>
            <h2 className="mt-6 font-display text-3xl text-amber-100">
              Agendamento <span className="gold-text italic">cancelado</span>
            </h2>
            <p className="mt-3 text-sm text-stone-400">
              O horário foi liberado. Se quiser remarcar, é só fazer um novo agendamento.
            </p>
            <a href="/" className="gold-btn mt-8 w-full" data-testid="cancel-rebook-link">
              Fazer novo agendamento
            </a>
          </div>
        )}
      </motion.div>
    </section>
  );
}
